import { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { formatPhoneAccount } from '@/constants/phoneDialCodes';
import { apiBuyerRegister, apiBuyerCaptchaSend, apiCaptchaCheck } from '@/service/api';
import { ChannelTypeEnum, BuyerOperationTypeEnum } from '@/service/api/types';
import { useTranslation } from 'react-i18next';

export default () => {
  const { t } = useTranslation()
  const [activeSignInType, setActiveSignInType] = useState('phone')
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [step, setStep] = useState(1); // 1: 验证码步骤, 2: 密码步骤
  const [accountInfo, setAccountInfo] = useState<{ account: string; captcha: string } | null>(null);
  const [form] = Form.useForm()
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  // 监听表单字段变化
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(() => {
      setIsButtonDisabled(false);
    }).catch(() => setIsButtonDisabled(true))
  }, [form, formValues])


  /**
   * 发送验证码
   */
  const sendCaptcha = async (account: string) => {
    

    if (countdown > 0) {
      return false;
    }

    try {
      setCodeLoading(true);
      const res = await apiBuyerCaptchaSend({
        account,
        operationType: BuyerOperationTypeEnum.REGISTER,
      });

      if (res.code === 0) {
        message.success(t('8c1f1d75'));
        // 开始倒计时
        let timer = 60;
        setCountdown(timer);
        const interval = setInterval(() => {
          timer--;
          setCountdown(timer);
          if (timer <= 0) {
            clearInterval(interval);
          }
        }, 1000);
        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      return false;
    } finally {
      setCodeLoading(false);
    }
  };

  /**
   * 验证验证码并进入下一步
   */
  const verifyCaptchaAndNext = (account: string, captcha: string) => {
    apiCaptchaCheck({
      account,
      captcha,
      operationType: BuyerOperationTypeEnum.REGISTER,
    }).then(res => {
      if(res.data) {
        setAccountInfo({ account, captcha });
        setStep(2);
        return true;
      } else {
        message.error(t('7015ab85'));
        return false;
      }
    })
  };

  /**
   * 返回上一步
   */
  const goBack = () => {
    setStep(1);
    setAccountInfo(null);
  };

  /**
   * 注册
   */
  const register = async (data: {
    password: string;
    confirmPassword: string;
    agreeProtocol: boolean;
  }) => {
    if (!accountInfo) {
      return false;
    }


    try {
      setLoading(true);
      const res = await apiBuyerRegister({
        account: accountInfo.account,
        password: data.password,
        confirmPassword: data.confirmPassword,
        captcha: accountInfo.captcha,
        channel: ChannelTypeEnum.WEB, // 根据实际渠道设置
        agreeProtocol: data.agreeProtocol,
      });

      if (res.code === 0) {
        message.success(t('d8f91061'));
        // 注册成功后跳转到登录页
        navigate('/signin', { replace: true });
        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const account =
      activeSignInType === 'phone'
        ? formatPhoneAccount(values.phoneDialCode, values.phone)
        : values.email;
    verifyCaptchaAndNext(account, values.code);
  }

  return {
    activeSignInType, setActiveSignInType,
    form,
    step,
    isButtonDisabled,
    loading,
    codeLoading,
    countdown,
    accountInfo,
    sendCaptcha,
    verifyCaptchaAndNext,
    goBack,
    register,
    handleSubmit,
  };
}